// The SME twin of server/src/utils/timeline/daily-timeline-notifications.ts:
// give it the day + the chase, get back the email and SMS for that chase's brand
// and type —
//
//   { sms: { message }, email: { brand, heading, template } }
//
// `template` is the FUNCTION the job calls with the customer data (see
// daily-job.ts: `timelineData?.email?.template({ ... })`), `heading` is the
// subject line. Returns null when there is nothing to send that day.
//
// The type axis, same as rensure's `chaseType`:
//
//   "auto"        auto-renewing, cash    → auto-renew/a21   (day 21 only, EMAIL ONLY)
//   "auto-dd"     auto-renewing, DD      → auto-renew/ad21  (day 21 only, EMAIL ONLY)
//   "cash"        pay in full, no auto   → pay-in-full/  (email + SMS)
//   "monthly-dd"  monthly Direct Debit   → monthly-dd/   (email + SMS)
//
// AUTO has its own table (`autoData`) — one email on day 21 and nothing else —
// so the day-by-day chase table (`timelineData`) only ever holds the two chased
// types, "cash" and "monthly-dd".
//
// `stage` picks the table: the pre-renewal chase (default), win-back after a
// lapse, the next-year anniversary, or the renewal-day documents email.

// ------------------------------------ DAY 21 --------------------------------------------------
import * as bcd_auto_renew_day21 from "./business-compare-direct/auto-renew/a21";
import * as bcd_auto_dd_day21 from "./business-compare-direct/auto-renew/ad21";
import * as bcd_pay_in_full_day21 from "./business-compare-direct/pay-in-full/ac21";
import * as bcd_monthly_dd_day21 from "./business-compare-direct/monthly-dd/na21";
import * as myb_auto_renew_day21 from "./mind-your-business/auto-renew/a21";
import * as myb_auto_dd_day21 from "./mind-your-business/auto-renew/ad21";
import * as myb_pay_in_full_day21 from "./mind-your-business/pay-in-full/ac21";
import * as myb_monthly_dd_day21 from "./mind-your-business/monthly-dd/na21";

// ------------------------------------ DAY 14 --------------------------------------------------
import * as bcd_pay_in_full_day14 from "./business-compare-direct/pay-in-full/ac14";
import * as bcd_monthly_dd_day14 from "./business-compare-direct/monthly-dd/na14";
import * as myb_pay_in_full_day14 from "./mind-your-business/pay-in-full/ac14";
import * as myb_monthly_dd_day14 from "./mind-your-business/monthly-dd/na14";

// ------------------------------------ DAY 10 --------------------------------------------------
import * as bcd_pay_in_full_day10 from "./business-compare-direct/pay-in-full/ac10";
import * as bcd_monthly_dd_day10 from "./business-compare-direct/monthly-dd/na10";
import * as myb_pay_in_full_day10 from "./mind-your-business/pay-in-full/ac10";
import * as myb_monthly_dd_day10 from "./mind-your-business/monthly-dd/na10";

// ------------------------------------ DAY 7 ---------------------------------------------------
import * as bcd_pay_in_full_day7 from "./business-compare-direct/pay-in-full/ac7";
import * as bcd_monthly_dd_day7 from "./business-compare-direct/monthly-dd/na7";
import * as myb_pay_in_full_day7 from "./mind-your-business/pay-in-full/ac7";
import * as myb_monthly_dd_day7 from "./mind-your-business/monthly-dd/na7";

// ------------------------------------ DAY 3 ---------------------------------------------------
import * as bcd_pay_in_full_day3 from "./business-compare-direct/pay-in-full/ac3";
import * as bcd_monthly_dd_day3 from "./business-compare-direct/monthly-dd/na3";
import * as myb_pay_in_full_day3 from "./mind-your-business/pay-in-full/ac3";
import * as myb_monthly_dd_day3 from "./mind-your-business/monthly-dd/na3";

// ------------------------------------ DAY 2 ---------------------------------------------------
import * as bcd_pay_in_full_day2 from "./business-compare-direct/pay-in-full/ac2";
import * as bcd_monthly_dd_day2 from "./business-compare-direct/monthly-dd/na2";
import * as myb_pay_in_full_day2 from "./mind-your-business/pay-in-full/ac2";
import * as myb_monthly_dd_day2 from "./mind-your-business/monthly-dd/na2";

// ------------------------------------ DAY 1 ---------------------------------------------------
import * as bcd_pay_in_full_day1 from "./business-compare-direct/pay-in-full/ac1";
import * as bcd_monthly_dd_day1 from "./business-compare-direct/monthly-dd/na1";
import * as myb_pay_in_full_day1 from "./mind-your-business/pay-in-full/ac1";
import * as myb_monthly_dd_day1 from "./mind-your-business/monthly-dd/na1";

// ------------------------------- WIN-BACK (days after lapse) ----------------------------------
import * as bcd_win_back_day3 from "./business-compare-direct/win-back-lapse/wb3";
import * as bcd_win_back_day7 from "./business-compare-direct/win-back-lapse/wb7";
import * as bcd_win_back_day14 from "./business-compare-direct/win-back-lapse/wb14";
import * as bcd_win_back_day28 from "./business-compare-direct/win-back-lapse/wb28";
import * as myb_win_back_day3 from "./mind-your-business/win-back-lapse/wb3";
import * as myb_win_back_day7 from "./mind-your-business/win-back-lapse/wb7";
import * as myb_win_back_day14 from "./mind-your-business/win-back-lapse/wb14";
import * as myb_win_back_day28 from "./mind-your-business/win-back-lapse/wb28";

// --------------------------- ANNIVERSARY (days before next renewal) ---------------------------
import * as bcd_anniversary_day30 from "./business-compare-direct/win-back-anniversary/an30";
import * as bcd_anniversary_day14 from "./business-compare-direct/win-back-anniversary/an14";
import * as bcd_anniversary_day7 from "./business-compare-direct/win-back-anniversary/an7";
import * as myb_anniversary_day30 from "./mind-your-business/win-back-anniversary/an30";
import * as myb_anniversary_day14 from "./mind-your-business/win-back-anniversary/an14";
import * as myb_anniversary_day7 from "./mind-your-business/win-back-anniversary/an7";

// ------------------------------------ RENEWAL DAY ---------------------------------------------
import * as bcd_renewed_day0 from "./business-compare-direct/renewed/ren0";
import * as myb_renewed_day0 from "./mind-your-business/renewed/ren0";

import { getBrand, type BrandKey } from "./brands";
import { anniversary, shortDate } from "./format";
import type { EmailData, PaymentType, QuoteRow } from "./types";

export type ChaseType = "auto" | "auto-dd" | "cash" | "monthly-dd";

export type Stage = "chase" | "win-back" | "anniversary" | "renewed";

/** The chase fields a template needs (a subset of the SMP Chase row). */
export type TimelineChase = {
  brand: BrandKey;
  /** True → the policy renews itself; it only ever gets the day-21 email. */
  autoRenew: boolean;
  paymentType: PaymentType;
  companyName?: string | null;
  foreName?: string | null;
  surname?: string | null;
  renewalDate: Date;
  instalments?: number | null;
  monthlyAmount?: number | null;
  totalPayable?: number | null;
  premium?: number | null;
  /** The chase's quotes — drives the comparison grid on priced emails. */
  quotes?: QuoteRow[];
  /** Marketing (win-back / anniversary) one-click opt-out, PECR. */
  unsubscribeUrl?: string | null;
  /** Policy reference — the "Pay online" CTA on the priced emails. */
  policyRef?: string | null;
  /** Which chase on that policy reference — the pay link's `?version=`. */
  versionRef?: string | null;
};

/** Which set of templates a chase reads from — rensure's `chaseType`. */
export function getChaseType(chase: TimelineChase): ChaseType {
  const dd = chase.paymentType === "INSTALMENTS";
  if (chase.autoRenew) return dd ? "auto-dd" : "auto";
  return dd ? "monthly-dd" : "cash";
}

/** The chase row as the templates want it (mirrors the SMP token resolution). */
export function toEmailData(chase: TimelineChase): EmailData {
  const quotes = chase.quotes ?? [];
  const selected = quotes.find((q) => q.selected);
  return {
    companyName:
      chase.companyName?.trim() ||
      [chase.foreName, chase.surname].filter(Boolean).join(" ").trim() ||
      "your business",
    renewalDate: chase.renewalDate,
    quotes,
    payment: {
      paymentType: chase.paymentType,
      instalments: chase.instalments ?? null,
      monthlyAmount: chase.monthlyAmount ?? null,
      // Prefer the chase's explicit total, else the selected quote, else premium.
      totalPayable: chase.totalPayable ?? selected?.total ?? chase.premium ?? 0,
    },
    paymentType: chase.paymentType,
    unsubscribeUrl: chase.unsubscribeUrl ?? undefined,
    policyRef: chase.policyRef ?? undefined,
    versionRef: chase.versionRef ?? undefined,
  };
}

export type TimelineResult = {
  sms: { message: string } | null;
  email:
    | {
        brand: BrandKey;
        heading: string;
        template: (data: EmailData) => string;
      }
    | undefined;
};

interface IgetDailyTimeLine {
  /** Days before renewal (or after lapse / before the anniversary). */
  day: number;
  chase: TimelineChase;
  /** Which table to read. Defaults to the pre-renewal chase timeline. */
  stage?: Stage;
}

const getDailyTimeLine = ({
  day,
  chase,
  stage = "chase",
}: IgetDailyTimeLine): TimelineResult | null => {
  const props = toEmailData(chase);
  const brand = getBrand(chase.brand)?.shortName;
  const callNumber = getBrand(chase.brand)?.callNumber;
  const expiryDate = shortDate(chase.renewalDate);
  const anniversaryDate = shortDate(anniversary(chase.renewalDate));

  // ------------------------------------ AUTO (day 21, email only) ------------------------------------
  // An auto-renewing policy is never chased: one reassuring email, no SMS, and
  // nothing at all on any other day — the cash one (a21) or the monthly Direct
  // Debit one (ad21, PremFina wording).

  const autoData = [
    {
      day: 21,
      emailContent: [
        {
          type: "auto",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_auto_renew_day21.subject(props),
              template: bcd_auto_renew_day21.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_auto_renew_day21.subject(props),
              template: myb_auto_renew_day21.html,
            },
          ],
        },
        {
          type: "auto-dd",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_auto_dd_day21.subject(props),
              template: bcd_auto_dd_day21.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_auto_dd_day21.subject(props),
              template: myb_auto_dd_day21.html,
            },
          ],
        },
      ],
      smsContent: null,
    },
  ];

  // ------------------------- CHASE (days before renewal) — cash + monthly-dd -------------------------

  const timelineData = [
    {
      day: 21,
      emailContent: [
        {
          type: "cash",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_pay_in_full_day21.subject(props),
              template: bcd_pay_in_full_day21.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_pay_in_full_day21.subject(props),
              template: myb_pay_in_full_day21.html,
            },
          ],
        },
        {
          type: "monthly-dd",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_monthly_dd_day21.subject(props),
              template: bcd_monthly_dd_day21.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_monthly_dd_day21.subject(props),
              template: myb_monthly_dd_day21.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand}: your business insurance renews ${expiryDate}. Call ${callNumber} to renew.`,
      },
    },
    {
      day: 14,
      emailContent: [
        {
          type: "cash",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_pay_in_full_day14.subject(props),
              template: bcd_pay_in_full_day14.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_pay_in_full_day14.subject(props),
              template: myb_pay_in_full_day14.html,
            },
          ],
        },
        {
          type: "monthly-dd",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_monthly_dd_day14.subject(props),
              template: bcd_monthly_dd_day14.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_monthly_dd_day14.subject(props),
              template: myb_monthly_dd_day14.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand}: reminder your cover renews ${expiryDate}. Renew now to keep your rate. Call ${callNumber}.`,
      },
    },
    {
      day: 10,
      emailContent: [
        {
          type: "cash",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_pay_in_full_day10.subject(props),
              template: bcd_pay_in_full_day10.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_pay_in_full_day10.subject(props),
              template: myb_pay_in_full_day10.html,
            },
          ],
        },
        {
          type: "monthly-dd",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_monthly_dd_day10.subject(props),
              template: bcd_monthly_dd_day10.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_monthly_dd_day10.subject(props),
              template: myb_monthly_dd_day10.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand}: policy renews ${expiryDate} & will NOT auto-renew. Call ${callNumber} to renew.`,
      },
    },
    {
      day: 7,
      emailContent: [
        {
          type: "cash",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_pay_in_full_day7.subject(props),
              template: bcd_pay_in_full_day7.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_pay_in_full_day7.subject(props),
              template: myb_pay_in_full_day7.html,
            },
          ],
        },
        {
          type: "monthly-dd",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_monthly_dd_day7.subject(props),
              template: bcd_monthly_dd_day7.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_monthly_dd_day7.subject(props),
              template: myb_monthly_dd_day7.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand}: 1 week left - cover expires ${expiryDate}. Renew to avoid a gap. Call ${callNumber}.`,
      },
    },
    {
      day: 3,
      emailContent: [
        {
          type: "cash",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_pay_in_full_day3.subject(props),
              template: bcd_pay_in_full_day3.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_pay_in_full_day3.subject(props),
              template: myb_pay_in_full_day3.html,
            },
          ],
        },
        {
          type: "monthly-dd",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_monthly_dd_day3.subject(props),
              template: bcd_monthly_dd_day3.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_monthly_dd_day3.subject(props),
              template: myb_monthly_dd_day3.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand} URGENT: cover expires ${expiryDate} & won't auto-renew. Action needed - call ${callNumber}.`,
      },
    },
    {
      day: 2,
      emailContent: [
        {
          type: "cash",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_pay_in_full_day2.subject(props),
              template: bcd_pay_in_full_day2.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_pay_in_full_day2.subject(props),
              template: myb_pay_in_full_day2.html,
            },
          ],
        },
        {
          type: "monthly-dd",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_monthly_dd_day2.subject(props),
              template: bcd_monthly_dd_day2.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_monthly_dd_day2.subject(props),
              template: myb_monthly_dd_day2.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand}: 48hrs left - cover expires ${expiryDate}. Last chance to renew. Call ${callNumber}.`,
      },
    },
    {
      day: 1,
      emailContent: [
        {
          type: "cash",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_pay_in_full_day1.subject(props),
              template: bcd_pay_in_full_day1.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_pay_in_full_day1.subject(props),
              template: myb_pay_in_full_day1.html,
            },
          ],
        },
        {
          type: "monthly-dd",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_monthly_dd_day1.subject(props),
              template: bcd_monthly_dd_day1.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_monthly_dd_day1.subject(props),
              template: myb_monthly_dd_day1.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand} FINAL NOTICE: cover expires midnight. Call ${callNumber} to renew today.`,
      },
    },
  ];

  // ---------------------------------- WIN-BACK (days after the lapse) ----------------------------------
  // The same voucher offer whatever the lapsed policy's type was.

  const winBackData = [
    {
      day: 3,
      emailContent: [
        {
          type: "all",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_win_back_day3.subject(props),
              template: bcd_win_back_day3.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_win_back_day3.subject(props),
              template: myb_win_back_day3.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand}: sorry to see you go! Come back & get a £25 Amazon voucher. Call ${callNumber}. Reply STOP to opt out`,
      },
    },
    {
      day: 7,
      emailContent: [
        {
          type: "all",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_win_back_day7.subject(props),
              template: bcd_win_back_day7.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_win_back_day7.subject(props),
              template: myb_win_back_day7.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand}: your business may be uninsured. Rejoin for a £25 Amazon voucher - call ${callNumber}. Reply STOP to opt out`,
      },
    },
    {
      day: 14,
      emailContent: [
        {
          type: "all",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_win_back_day14.subject(props),
              template: bcd_win_back_day14.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_win_back_day14.subject(props),
              template: myb_win_back_day14.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand}: your £25 Amazon voucher is still waiting when you re-insure. Call ${callNumber}. Reply STOP to opt out`,
      },
    },
    {
      day: 28,
      emailContent: [
        {
          type: "all",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_win_back_day28.subject(props),
              template: bcd_win_back_day28.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_win_back_day28.subject(props),
              template: myb_win_back_day28.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand}: final reminder - £25 Amazon voucher when you rejoin. Call ${callNumber}. Reply STOP to opt out`,
      },
    },
  ];

  // ------------------------- ANNIVERSARY (days before next year's renewal) -------------------------
  // Day 30 is email only — there is no SMS at a month out.

  const anniversaryData = [
    {
      day: 30,
      emailContent: [
        {
          type: "all",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_anniversary_day30.subject(props),
              template: bcd_anniversary_day30.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_anniversary_day30.subject(props),
              template: myb_anniversary_day30.html,
            },
          ],
        },
      ],
      smsContent: null,
    },
    {
      day: 14,
      emailContent: [
        {
          type: "all",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_anniversary_day14.subject(props),
              template: bcd_anniversary_day14.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_anniversary_day14.subject(props),
              template: myb_anniversary_day14.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand}: business insurance due around ${anniversaryDate}? Quote + £25 Amazon voucher: ${callNumber}. Reply STOP to opt out`,
      },
    },
    {
      day: 7,
      emailContent: [
        {
          type: "all",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_anniversary_day7.subject(props),
              template: bcd_anniversary_day7.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_anniversary_day7.subject(props),
              template: myb_anniversary_day7.html,
            },
          ],
        },
      ],
      smsContent: {
        message: `${brand}: renewing this week? See if we can beat it + £25 Amazon voucher. Call ${callNumber}. Reply STOP to opt out`,
      },
    },
  ];

  // ------------------------------------ RENEWAL DAY (email only) ------------------------------------

  const renewedData = [
    {
      day: 0,
      emailContent: [
        {
          type: "all",
          template: [
            {
              brand: "BUSINESS_COMPARE_DIRECT",
              heading: bcd_renewed_day0.subject(props),
              template: bcd_renewed_day0.html,
            },
            {
              brand: "MIND_YOUR_BUSINESS",
              heading: myb_renewed_day0.subject(props),
              template: myb_renewed_day0.html,
            },
          ],
        },
      ],
      smsContent: null,
    },
  ];

  // Win-back / anniversary / renewed go to everyone, so they file under "all".
  const chaseType = stage === "chase" ? getChaseType(chase) : "all";

  const stageData =
    stage === "win-back"
      ? winBackData
      : stage === "anniversary"
      ? anniversaryData
      : stage === "renewed"
      ? renewedData
      : // The chase stage splits by type: AUTO (cash or DD) has its own one-row
        // table, the two chased types (cash / monthly-dd) share the day-by-day one.
      chaseType.startsWith("auto")
      ? autoData
      : timelineData;

  const data = stageData.find((item) => item.day === day);

  // NB: test the row, not `data.day` — renewal day IS day 0, which is falsy.
  if (!data) {
    return null;
  }

  const emailData = data?.emailContent?.find((item) => item.type === chaseType);

  if (!emailData) {
    console.log(`No ${stage} template for type ${chaseType} on day ${day}`);
    return null;
  }

  return {
    // AUTO is email only — its table carries no SMS at all.
    sms: data?.smsContent ?? null,
    email: emailData?.template?.find((item) => item.brand === chase?.brand) as
      | TimelineResult["email"]
      | undefined,
  };
};

export default getDailyTimeLine;
